import { create } from 'zustand';
import { employeesApi } from '../../../api/api';
import type { EmployeeResponse, EmployeeServiceResponse, CreateEmployeeRequest, UpdateEmployeeRequest } from '../../../api/generated';

type EmployeeState = {
  employees: EmployeeResponse[];
  loading: boolean;
  error: string | null;

  // Actions
  loadEmployees: (filters?: {
    page?: number;
    limit?: number;
    q?: string;
    status?: 'active' | 'inactive';
    salonId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => Promise<void>;

  getEmployeeServices: (employeeId: string) => Promise<EmployeeServiceResponse[]>;
  createEmployee: (data: CreateEmployeeRequest) => Promise<void>;
  updateEmployee: (id: string, data: UpdateEmployeeRequest) => Promise<void>;
  deleteEmployee: (id: string) => Promise<void>;

  clearError: () => void;
};

export const useEmployeeStore = create<EmployeeState>(set => ({
  employees: [],
  loading: false,
  error: null,

  loadEmployees: async (filters = {}) => {
    set({ loading: true, error: null });

    try {
      const response = await employeesApi.getEmployees({
        page: filters.page || 1,
        limit: filters.limit || 100,
        q: filters.q,
        status: filters.status,
        salonId: filters.salonId,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });

      const employees = response.data.data;
      set({ employees, loading: false });
    } catch (error) {
      console.error('Error loading employees:', error);
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání zaměstnanců',
        loading: false,
      });
    }
  },

  getEmployeeServices: async employeeId => {
    try {
      const response = await employeesApi.getEmployeeServices({ id: employeeId });
      return response.data;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání služeb zaměstnance',
      });
      throw error;
    }
  },

  createEmployee: async (data: CreateEmployeeRequest) => {
    set({ loading: true, error: null });
    try {
      await employeesApi.createEmployee({ createEmployeeRequest: data });
      set({ loading: false });
    } catch (error) {
      console.error('Error creating employee:', error);
      set({
        error: error instanceof Error ? error.message : 'Chyba při vytváření zaměstnance',
        loading: false,
      });
      throw error;
    }
  },

  updateEmployee: async (id: string, data: UpdateEmployeeRequest) => {
    set({ loading: true, error: null });
    try {
      await employeesApi.updateEmployee({ id, updateEmployeeRequest: data });
      set({ loading: false });
    } catch (error) {
      console.error('Error updating employee:', error);
      set({
        error: error instanceof Error ? error.message : 'Chyba při úpravě zaměstnance',
        loading: false,
      });
      throw error;
    }
  },

  deleteEmployee: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await employeesApi.deleteEmployee({ id });
      set({ loading: false });
    } catch (error) {
      console.error('Error deleting employee:', error);
      set({
        error: error instanceof Error ? error.message : 'Chyba při mazání zaměstnance',
        loading: false,
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
